import {
  IconArrowRight,
  TableContainer,
  TableRowContainer,
  TableShellTitle,
  TableShellValue,
} from "@frontend/common";

import { getAxisRoleLabel, useAxisRoleQuery } from "@api/axisRole/axisRole";
import EditableTextContainer from "@EditArticleContainer/shared/components/EditableTextContainer/EditableTextContainer";
import {
  REF_CODE_FINANCIAL_TERMS_LASC_MILESTONE_DETAIL,
  REF_CODE_FINANCIAL_TERMS_LASC_MILESTONE_VALUE,
  REF_CODE_FINANCIAL_TERMS_LASC_OTHER_DETAIL,
  REF_CODE_FINANCIAL_TERMS_LASC_OTHER_VALUE,
  REF_CODE_FINANCIAL_TERMS_LASC_ROYALTY_DETAIL,
  REF_CODE_FINANCIAL_TERMS_LASC_ROYALTY_RATE,
  REF_CODE_FINANCIAL_TERMS_LASC_TOTAL,
  REF_CODE_FINANCIAL_TERMS_LASC_UPFRONT,
} from "@EditArticleContainer/shared/libs/refCode/constants/refCode.constants";
import { createRefCode } from "@EditArticleContainer/shared/libs/refCode/utils/createRefCode";
import { MainFinancialTermsTableItem } from "@EditArticleContainer/shared/types/paragraphContents/contents/financialTermsLASCContent.types";

import * as S from "./FinancialTermsLASCTableGroup.style";
import {
  milestoneDetailLabels,
  milestoneDetailText,
  milestoneValueLabels,
  milestoneValueText,
  otherDetailLabels,
  otherDetailText,
  otherValueLabels,
  otherValueText,
  payingAxisValues,
  receivingAxisValues,
  royaltyDetailLabels,
  royaltyDetailText,
  royaltyRateLabels,
  royaltyRateText,
  totalLabels,
  totalText,
  upfrontLabels,
  upfrontText,
} from "../../utils/getFinancialTermsLASCShellValue";

interface Props {
  item: MainFinancialTermsTableItem;
  tableIndex: number;
}

const VALUE_SHELL_PADDING = "0.2rem";
const EDITABLE_TEXT_STYLE = { padding: "0.8rem", borderRadius: "0" };
const TITLE_WIDTH = "12rem";

const toPartnerLabel = (axis: string) => {
  const map: Record<string, string> = {
    A: "Partner A",
    B: "Partner B",
    C: "Partner C",
  };
  return map[axis] ?? `Partner ${axis}`;
};

const FinancialTermsLASCTableGroup = ({ item, tableIndex }: Props) => {
  const { data: axisRole } = useAxisRoleQuery();
  const paying = payingAxisValues(item);
  const receiving = receivingAxisValues(item);
  const milestones = item["Milestone Payment And Detail"];
  const others = item["Other Payment And Detail"];
  const royalties = item["Royalty(LA&SC)"];

  // 행 내 모든 값이 비어있으면 표출하지 않는다. (refKey 정합성을 위해 원본 index 유지)
  const showUpfront = upfrontText(item).trim() !== "";
  const showTotal = totalText(item).trim() !== "";
  const visibleMilestones = milestones
    .map((milestone, index) => ({ milestone, index }))
    .filter(
      ({ milestone }) =>
        milestoneValueText(milestone).trim() !== "" ||
        milestoneDetailText(milestone).trim() !== "",
    );
  const visibleOthers = others
    .map((other, index) => ({ other, index }))
    .filter(
      ({ other }) =>
        otherValueText(other).trim() !== "" ||
        otherDetailText(other).trim() !== "",
    );
  const visibleRoyalties = royalties
    .map((royalty, index) => ({ royalty, index }))
    .filter(
      ({ royalty }) =>
        royaltyRateText(royalty).trim() !== "" ||
        royaltyDetailText(royalty).trim() !== "",
    );

  return (
    <S.TableGroupWrapper>
      {(paying.length > 0 || receiving.length > 0) && (
        <S.FlowRow>
          {paying.length > 0 && (
            <S.AxisChipsGroup>
              {paying.map((axis) => {
                const role = getAxisRoleLabel(axisRole, axis);
                return (
                  <S.AxisChip key={axis}>
                    {toPartnerLabel(axis)}
                    {role && <S.AxisRole>({role})</S.AxisRole>}
                  </S.AxisChip>
                );
              })}
            </S.AxisChipsGroup>
          )}
          {paying.length > 0 && receiving.length > 0 && (
            <S.ArrowIcon>
              <IconArrowRight width={20} height={20} color="#7c899c" />
            </S.ArrowIcon>
          )}
          {receiving.length > 0 && (
            <S.AxisChipsGroup>
              {receiving.map((axis) => {
                const role = getAxisRoleLabel(axisRole, axis);
                return (
                  <S.AxisChip key={axis}>
                    {toPartnerLabel(axis)}
                    {role && <S.AxisRole>({role})</S.AxisRole>}
                  </S.AxisChip>
                );
              })}
            </S.AxisChipsGroup>
          )}
        </S.FlowRow>
      )}

      <TableContainer>
        <TableRowContainer>
          <TableShellTitle width={TITLE_WIDTH}>{null}</TableShellTitle>
          <TableShellTitle>Value</TableShellTitle>
          <TableShellTitle>Detail</TableShellTitle>
        </TableRowContainer>

        {showUpfront && (
          <TableRowContainer>
            <TableShellTitle width={TITLE_WIDTH}>Upfront</TableShellTitle>
            <TableShellValue padding={VALUE_SHELL_PADDING}>
              <EditableTextContainer
                item={{
                  text: upfrontText(item),
                  labels: upfrontLabels(item),
                }}
                refKey={createRefCode(
                  REF_CODE_FINANCIAL_TERMS_LASC_UPFRONT,
                  tableIndex,
                )}
                canTextEdit
                style={EDITABLE_TEXT_STYLE}
              />
            </TableShellValue>
            <TableShellValue padding={VALUE_SHELL_PADDING} isEditable={false}>
              {null}
            </TableShellValue>
          </TableRowContainer>
        )}

        {visibleMilestones.map(({ milestone, index: i }, visibleIndex) => (
          <TableRowContainer key={`milestone-${String(i)}`}>
            <TableShellTitle width={TITLE_WIDTH}>
              {visibleMilestones.length > 1
                ? `Milestone ${String(visibleIndex + 1)}`
                : "Milestone"}
            </TableShellTitle>
            <TableShellValue padding={VALUE_SHELL_PADDING}>
              <EditableTextContainer
                item={{
                  text: milestoneValueText(milestone),
                  labels: milestoneValueLabels(milestone),
                }}
                refKey={createRefCode(
                  REF_CODE_FINANCIAL_TERMS_LASC_MILESTONE_VALUE,
                  tableIndex,
                  i,
                )}
                canTextEdit
                style={EDITABLE_TEXT_STYLE}
              />
            </TableShellValue>
            <TableShellValue padding={VALUE_SHELL_PADDING}>
              <EditableTextContainer
                item={{
                  text: milestoneDetailText(milestone),
                  labels: milestoneDetailLabels(milestone),
                }}
                refKey={createRefCode(
                  REF_CODE_FINANCIAL_TERMS_LASC_MILESTONE_DETAIL,
                  tableIndex,
                  i,
                )}
                canTextEdit
                style={EDITABLE_TEXT_STYLE}
              />
            </TableShellValue>
          </TableRowContainer>
        ))}

        {visibleOthers.map(({ other, index: i }, visibleIndex) => (
          <TableRowContainer key={`other-${String(i)}`}>
            <TableShellTitle width={TITLE_WIDTH}>
              {visibleOthers.length > 1
                ? `Other ${String(visibleIndex + 1)}`
                : "Other"}
            </TableShellTitle>
            <TableShellValue padding={VALUE_SHELL_PADDING}>
              <EditableTextContainer
                item={{
                  text: otherValueText(other),
                  labels: otherValueLabels(other),
                }}
                refKey={createRefCode(
                  REF_CODE_FINANCIAL_TERMS_LASC_OTHER_VALUE,
                  tableIndex,
                  i,
                )}
                canTextEdit
                style={EDITABLE_TEXT_STYLE}
              />
            </TableShellValue>
            <TableShellValue padding={VALUE_SHELL_PADDING}>
              <EditableTextContainer
                item={{
                  text: otherDetailText(other),
                  labels: otherDetailLabels(other),
                }}
                refKey={createRefCode(
                  REF_CODE_FINANCIAL_TERMS_LASC_OTHER_DETAIL,
                  tableIndex,
                  i,
                )}
                canTextEdit
                style={EDITABLE_TEXT_STYLE}
              />
            </TableShellValue>
          </TableRowContainer>
        ))}

        {visibleRoyalties.map(({ royalty, index: i }, visibleIndex) => (
          <TableRowContainer key={`royalty-${String(i)}`}>
            <TableShellTitle width={TITLE_WIDTH}>
              {visibleRoyalties.length > 1
                ? `Royalty ${String(visibleIndex + 1)}`
                : "Royalty"}
            </TableShellTitle>
            <TableShellValue padding={VALUE_SHELL_PADDING}>
              <EditableTextContainer
                item={{
                  text: royaltyRateText(royalty),
                  labels: royaltyRateLabels(royalty),
                }}
                refKey={createRefCode(
                  REF_CODE_FINANCIAL_TERMS_LASC_ROYALTY_RATE,
                  tableIndex,
                  i,
                )}
                canTextEdit
                style={EDITABLE_TEXT_STYLE}
              />
            </TableShellValue>
            <TableShellValue padding={VALUE_SHELL_PADDING}>
              <EditableTextContainer
                item={{
                  text: royaltyDetailText(royalty),
                  labels: royaltyDetailLabels(royalty),
                }}
                refKey={createRefCode(
                  REF_CODE_FINANCIAL_TERMS_LASC_ROYALTY_DETAIL,
                  tableIndex,
                  i,
                )}
                canTextEdit
                style={EDITABLE_TEXT_STYLE}
              />
            </TableShellValue>
          </TableRowContainer>
        ))}

        {showTotal && (
          <TableRowContainer>
            <TableShellTitle width={TITLE_WIDTH}>Total</TableShellTitle>
            <TableShellValue padding={VALUE_SHELL_PADDING}>
              <EditableTextContainer
                item={{
                  text: totalText(item),
                  labels: totalLabels(item),
                }}
                refKey={createRefCode(
                  REF_CODE_FINANCIAL_TERMS_LASC_TOTAL,
                  tableIndex,
                )}
                canTextEdit
                style={EDITABLE_TEXT_STYLE}
              />
            </TableShellValue>
            <TableShellValue padding={VALUE_SHELL_PADDING} isEditable={false}>
              {null}
            </TableShellValue>
          </TableRowContainer>
        )}
      </TableContainer>
    </S.TableGroupWrapper>
  );
};

export default FinancialTermsLASCTableGroup;
